import axios from 'axios';

import { assertClientSide, webpack } from '@dr.pogodin/react-utils';

import type {
  GetExperienceResT,
  PatchExperienceBodyT,
  PostExperienceBodyT,
  PostExperienceResT,
  SuggestExperienceBodyT,
  SuggestExperienceResT,
} from 'server/controllers/experience';

import type * as SST from 'server/services/experience';

import {
  type BodyPositionT,
  type ExperienceT,
  type LifeMomentIdT,
  P_API_EXPERIENCES,
  P_API_EXPERIENCES_SUGGEST,
  type SessionKindT,
  type VideoIntentGroupT,
  type VideoIntentT,
  getConfig,
} from 'utils';

const BASE = P_API_EXPERIENCES;

const ss = webpack.requireWeak<typeof SST>('server/services/experience', import.meta.dirname);

/** Returns ID of the created document. */
export async function createExperience(
  experience: ExperienceT,
): Promise<string> {
  const videos = Array<string>(experience.videos.length);
  for (let i = 0; i < videos.length; ++i) {
    const video = experience.videos[i];
    if (!video?.id) throw Error('Internal error');

    videos[i] = video.id;
  }

  const config = await getConfig();

  const res = await axios.post<PostExperienceResT>(BASE, {
    _csrf: config.CSRF,
    data: {
      createdBy: experience.createdBy,
      title: experience.title ?? undefined,
      videos,
    },
  } satisfies PostExperienceBodyT);
  return res.data;
}

type GetAiExperienceArgT = {
  bodyPositions: BodyPositionT[] | null;
  checkInTranscriptId: string;
  desiredState: string | undefined;
  durationMin: number;
  environment: string;
  excludedArtistIds: string[] | undefined;
  intent: VideoIntentT | undefined;
  intentGroup: VideoIntentGroupT | undefined;
  lifeMoment: LifeMomentIdT;
  modalities: string[];
  selectedArtistIds: string[] | undefined;
  userDateTime: string;
};

type GetAiExperienceResT = {
  experience: ExperienceT;
  sessionKind: SessionKindT;
};

/**
 * Gets the AI-generated experience, based on the specified wellness check-in
 * questionnaire.
 */
export async function getAiExperience({
  bodyPositions,
  checkInTranscriptId,
  desiredState,
  durationMin,
  environment,
  excludedArtistIds,
  intent,
  intentGroup,
  lifeMoment,
  modalities,
  selectedArtistIds,
  userDateTime,
}: GetAiExperienceArgT): Promise<GetAiExperienceResT> {
  assertClientSide();

  const config = getConfig(true);

  const res = await axios.post<SuggestExperienceResT>(
    P_API_EXPERIENCES_SUGGEST,
    {
      _csrf: config.CSRF,
      bodyPositions,
      checkInTranscriptId,
      desiredState: desiredState ?? null,
      durationMin,
      environment,
      excludedArtistIds,
      intent: intent ?? null,
      intentGroup: intentGroup ?? null,
      lifeMoment,
      modalities,
      selectedArtistIds,
      userDateTime,
    } satisfies SuggestExperienceBodyT,
  );

  return res.data;
}

/** Gets experience by ID. */
export async function getExperience(id: string): Promise<ExperienceT> {
  if (ss) {
    const res = await ss.getExperience(id);
    if (!res) throw Error('Experience not found');
    return res;
  }

  const { data } = await axios<GetExperienceResT>(`${BASE}/${id}`);

  return data;
}

/**
 * Sets the title of an already-saved experience (e.g. to persist the title
 * shown in the builder UI, once known, for experiences saved without one -
 * see the "low quality" path of generateExperienceV2()).
 */
export async function setExperienceTitle(
  id: string,
  title: string,
): Promise<void> {
  const config = await getConfig();
  await axios.patch(`${BASE}/${id}`, {
    _csrf: config.CSRF,
    title,
  } satisfies PatchExperienceBodyT);
}
